/**
 * Field override + custom field + UI preset client (P2-S5).
 *
 * 4 layer override: platform → tenant → role → personal.
 * - Admin: PUT /api/v1/admin/forms/{code}/fields/{name}/override
 * - User: PUT /api/v1/me/forms/{code}/fields/{name}/override
 */
import { api } from "./client";

// ============================================================================
// Field overrides
// ============================================================================
export type Layer = "platform" | "tenant" | "role" | "personal";

export interface FieldOverrideMeta {
  layer: Layer;
  layer_id: string | null;
  overrides: Record<string, unknown>;
  created_by: string | null;
  updated_at: string;
}

export interface UpsertOverrideBody {
  layer: Exclude<Layer, "personal">; // personal qua /me/
  layer_id?: string | null;
  overrides: Record<string, unknown>;
}

export interface UpsertOverrideResult {
  id: string;
  layer: string;
  layer_id: string | null;
  applied_keys: string[];
  rejected_keys: string[];
}

// ============================================================================
// Custom fields
// ============================================================================
export interface CustomFieldOut {
  id: string;
  field_name: string;
  schema: Record<string, unknown>;
  erp_mapping: Record<string, unknown> | null;
  ui_only: boolean;
  created_at: string;
}

export interface CreateCustomFieldBody {
  field_name: string;
  schema: Record<string, unknown>;
  erp_mapping?: Record<string, unknown> | null;
  ui_only?: boolean;
}

// ============================================================================
// UI Presets
// ============================================================================
export type PresetType = "table" | "chart" | "kpi" | "response_template";

export interface UIPresetOut {
  id: string;
  preset_type: PresetType;
  preset_key: string;
  config: Record<string, unknown>;
  is_default: boolean;
  updated_at: string;
}

export interface UpsertUIPresetBody {
  preset_type: PresetType;
  preset_key: string;
  config: Record<string, unknown>;
  is_default?: boolean;
}

// ============================================================================
// Client
// ============================================================================
export const overridesApi = {
  // -------- Admin: field overrides --------
  listFieldOverrides: (code: string, fieldName: string) =>
    api.get<FieldOverrideMeta[]>(
      `/api/v1/admin/forms/${code}/fields/${fieldName}/overrides`,
    ),
  upsertOverride: (code: string, fieldName: string, body: UpsertOverrideBody) =>
    api.put<UpsertOverrideResult>(
      `/api/v1/admin/forms/${code}/fields/${fieldName}/override`,
      body,
    ),
  deleteOverride: (
    code: string,
    fieldName: string,
    layer: Exclude<Layer, "personal">,
    layerId?: string,
  ) =>
    api.delete<void>(
      `/api/v1/admin/forms/${code}/fields/${fieldName}/override`,
      { query: { layer, layer_id: layerId } },
    ),

  // -------- Admin: custom fields --------
  listCustomFields: (code: string) =>
    api.get<CustomFieldOut[]>(`/api/v1/admin/forms/${code}/custom-fields`),
  createCustomField: (code: string, body: CreateCustomFieldBody) =>
    api.post<CustomFieldOut>(`/api/v1/admin/forms/${code}/custom-fields`, body),
  deleteCustomField: (code: string, fieldName: string) =>
    api.delete<void>(`/api/v1/admin/forms/${code}/custom-fields/${fieldName}`),

  // -------- Personal: own override --------
  upsertPersonalOverride: (
    code: string,
    fieldName: string,
    overrides: Record<string, unknown>,
  ) =>
    api.put<UpsertOverrideResult>(
      `/api/v1/me/forms/${code}/fields/${fieldName}/override`,
      { overrides },
    ),
  deletePersonalOverride: (code: string, fieldName: string) =>
    api.delete<void>(`/api/v1/me/forms/${code}/fields/${fieldName}/override`),

  // -------- UI Presets --------
  listPresets: (presetType?: PresetType) =>
    api.get<UIPresetOut[]>("/api/v1/me/ui-presets", {
      query: presetType ? { preset_type: presetType } : undefined,
    }),
  upsertPreset: (body: UpsertUIPresetBody) =>
    api.post<UIPresetOut>("/api/v1/me/ui-presets", body),
  deletePreset: (presetType: PresetType, presetKey: string) =>
    api.delete<void>(`/api/v1/me/ui-presets/${presetType}/${presetKey}`),
};
